// ─── Computation Cache Layer ─────────────────────────────────────────────────
// Provides memoization for expensive calculation functions.
// Uses a content-hash approach: serializes inputs to a key string and returns
// cached results when the key matches. Avoids React useMemo's reference-equality
// trap where unchanged data in new arrays/objects triggers recomputation.

const _caches = new Map();

/**
 * Memoize an expensive function by its serialized arguments.
 * @param {string} namespace - unique identifier for this cache slot
 * @param {Function} fn - the expensive function to call
 * @param {Array} deps - dependency values (will be JSON-serialized for comparison)
 * @param {Object} [opts] - options
 * @param {number} [opts.maxEntries=3] - max cached entries per namespace (LRU eviction)
 * @returns {*} cached or freshly computed result
 */
export function cached(namespace, fn, deps, opts = {}) {
  const maxEntries = opts.maxEntries || 3;
  if (!_caches.has(namespace)) _caches.set(namespace, []);
  const entries = _caches.get(namespace);

  // Build key from deps — fast shallow comparison first, JSON fallback
  const key = stableKey(deps);

  // Check cache
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].key === key) {
      // Move to front (most recently used)
      if (i > 0) {
        const hit = entries.splice(i, 1)[0];
        entries.unshift(hit);
      }
      return entries[0].value;
    }
  }

  // Cache miss — compute
  const value = fn();

  // Store result
  entries.unshift({ key, value });
  while (entries.length > maxEntries) entries.pop();

  return value;
}

/**
 * Build a stable string key from dependency values.
 * Handles primitives quickly, falls back to JSON for objects.
 */
function stableKey(deps) {
  const parts = [];
  for (let i = 0; i < deps.length; i++) {
    const d = deps[i];
    if (d === null || d === undefined) { parts.push(String(d)); continue; }
    const t = typeof d;
    if (t === "string" || t === "number" || t === "boolean") {
      parts.push(String(d));
    } else if (d instanceof Date) {
      parts.push(d.getTime().toString());
    } else if (Array.isArray(d)) {
      // For arrays, use length + shallow hash of first/last items
      // Full JSON only for small arrays
      if (d.length <= 20) {
        parts.push(JSON.stringify(d));
      } else {
        parts.push(`[${d.length}:${shallowHash(d[0])}:${shallowHash(d[d.length - 1])}:${shallowHash(d[Math.floor(d.length / 2)])}]`);
      }
    } else {
      parts.push(JSON.stringify(d));
    }
  }
  return parts.join("|");
}

function shallowHash(obj) {
  if (obj === null || obj === undefined) return "nil";
  if (typeof obj !== "object") return String(obj);
  const keys = Object.keys(obj);
  return `{${keys.length}:${keys.slice(0, 5).map(k => `${k}=${typeof obj[k] === "object" ? "[obj]" : obj[k]}`).join(",")}}`;
}

/**
 * Clear all caches or a specific namespace.
 */
export function clearCache(namespace) {
  if (namespace) {
    _caches.delete(namespace);
  } else {
    _caches.clear();
  }
}

/**
 * React hook version — drop-in replacement for useMemo with content-based caching.
 * Import: import { useCached } from "../lib/computeCache.js";
 * Usage: const result = useCached("dailyData", () => computeDailyData(args), [dep1, dep2, ...]);
 */
export function useCached(namespace, fn, deps) {
  return cached(namespace, fn, deps);
}
